<template>
  <Sidebar :collapsible="'icon'">
    <SidebarHeader class="flex w-full">
      <div
        class="flex h-[56px] w-full items-center py-2"
        :class="{
          'pl-4 pr-0': !collapsed,
          'justify-center': collapsed,
          'justify-between': !collapsed,
        }"
      >
        <h1 :class="{ hidden: collapsed }">
          <img
            v-if="isVnimpexOrganization"
            src="/img/vnimpex-logo.png"
            alt="VN-Impex Logo"
            width="40"
            height="40"
          />
          <svg
            v-else
            width="154"
            height="40"
            viewBox="0 0 1000 260"
            xmlns="http://www.w3.org/2000/svg"
          >
            <use xlink:href="/img/digitax-logo.svg#logo" />
          </svg>
        </h1>
        <Button
          v-if="isMobile === false"
          variant="ghost"
          size="sm"
          @click="setOpen(!open)"
        >
          <span class="sr-only">{{ collapsed ? "Open" : "Hide" }} sidebar</span>
          <ChevronRight v-if="collapsed" />
          <ChevronLeft v-else />
        </Button>
      </div>
    </SidebarHeader>

    <SidebarContent class="flex-grow gap-2">
      <SidebarGroup>
        <SidebarGroupLabel>{{ t("sidebar.application") }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in navigationEntries" :key="item.key">
              <div class="relative">
                <SidebarMenuButton as-child :is-active="isActiveRoute(item.to)">
                  <NuxtLink :to="item.to">
                    <component :is="item.icon" />
                    <span>{{ t(item.name) }}</span>
                  </NuxtLink>
                </SidebarMenuButton>
                <!-- Toggle action for submenu -->
                <SidebarMenuAction
                  v-if="item.children"
                  type="button"
                  :aria-label="
                    t(
                      isGroupOpen(item)
                        ? 'navigation.collapse'
                        : 'navigation.expand',
                    )
                  "
                  @click.stop="toggleGroup(item.key)"
                >
                  <ChevronDown
                    class="transition-transform duration-200"
                    :class="{ 'rotate-180': isGroupOpen(item) }"
                  />
                </SidebarMenuAction>
              </div>
              <transition name="fade" mode="out-in">
                <SidebarMenuSub
                  v-if="item.children && isGroupOpen(item)"
                  key="sub"
                >
                  <SidebarMenuSubItem
                    v-for="child in item.children"
                    :key="child.key"
                  >
                    <SidebarMenuSubButton
                      as-child
                      :is-active="isActiveRoute(child.to)"
                    >
                      <NuxtLink :to="child.to" class="flex items-center gap-2">
                        <span>{{ t(child.name) }}</span>
                      </NuxtLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </transition>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  </Sidebar>
</template>

<script setup lang="ts">
import { useSidebar } from "./ui/sidebar";
import SidebarGroup from "./ui/sidebar/SidebarGroup.vue";
import {
  Home,
  GitBranch,
  MessagesSquare,
  FileText,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Settings,
  Ship,
  Sparkles,
  ClipboardList,
  Workflow,
  ListChecks,
  ScrollText,
} from "lucide-vue-next";
import { TEMPLATES } from "~/types/procedureBuilder";
import SidebarMenuAction from "./ui/sidebar/SidebarMenuAction.vue";

const { organizationId } = useOrganizationId();
const route = useRoute();
const { t } = useI18n();

// Org 1 is the real VN-IMPEX pilot; every other org (Unternehmen X demo
// included) shows the generic Digitax logo.
const isVnimpexOrganization = computed(() => organizationId.value === 1);

const { state, open, setOpen, isMobile } = useSidebar();
const collapsed = computed(() => state.value === "collapsed");

const isActiveRoute = (to: string) => {
  return route.path === to || route.path.startsWith(to + "/");
};

type NavigationEntry = {
  key: string;
  to: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any; // Lucide icon component
  children?: {
    key: string;
    to: string;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
  }[];
};

const navigationEntries = computed((): NavigationEntry[] => [
  {
    key: "dashboard",
    to: `/organizations/${organizationId.value}`,
    name: "navigation.dashboard",
    icon: Home,
  },

  {
    key: "companyProfile",
    to: `/organizations/${organizationId.value}/profile`,
    name: "navigation.companyProfile",
    icon: Settings,
    children: [
      {
        key: "basic",
        to: `/organizations/${organizationId.value}/profile/basic`,
        name: "companyProfile.basic.title",
      },
      {
        key: "goals",
        to: `/organizations/${organizationId.value}/profile/goals`,
        name: "companyProfile.goals.title",
      },
      {
        key: "it-basics",
        to: `/organizations/${organizationId.value}/profile/it-basics`,
        name: "companyProfile.itBasics.title",
      },
      {
        key: "it-systems",
        to: `/organizations/${organizationId.value}/profile/it-systems`,
        name: "companyProfile.itSystems.title",
      },
      {
        key: "tax",
        to: `/organizations/${organizationId.value}/profile/tax`,
        name: "companyProfile.tax.title",
      },
      {
        key: "organigram",
        to: `/organizations/${organizationId.value}/profile/organigram`,
        name: "companyProfile.organigram.title",
      },
    ],
  },
  {
    key: "documents",
    to: `/organizations/${organizationId.value}/documents`,
    name: "navigation.documents",
    icon: FileText,
  },
  {
    key: "process",
    to: `/organizations/${organizationId.value}/processes`,
    name: "navigation.process",
    icon: GitBranch,
  },
  {
    key: "risks",
    to: `/organizations/${organizationId.value}/risks`,
    name: "navigation.risks",
    icon: AlertTriangle,
  },
  {
    key: "invoiceCheckLog",
    to: `/organizations/${organizationId.value}/invoice-check-log`,
    name: "navigation.invoiceCheckLog",
    icon: ScrollText,
  },
  {
    key: "controlPicker",
    to: `/organizations/${organizationId.value}/control-picker`,
    name: "navigation.controlPicker",
    icon: ListChecks,
  },
  {
    key: "procedureBuilder",
    to: `/organizations/${organizationId.value}/procedure-builder`,
    name: "navigation.procedureBuilder",
    icon: ClipboardList,
  },
  {
    key: "flowBuilder",
    to: `/organizations/${organizationId.value}/flow-builder`,
    name: "navigation.flowBuilder",
    icon: Workflow,
    children: TEMPLATES.map((tpl) => ({
      key: tpl.key,
      to: `/organizations/${organizationId.value}/flow-builder/${tpl.key}`,
      name: `procedureBuilder.templates.${tpl.key}.title`,
    })),
  },
  {
    key: "conversations",
    to: `/organizations/${organizationId.value}/conversations`,
    name: "navigation.conversations",
    icon: MessagesSquare,
  },
  {
    key: "onboarding",
    to: `/organizations/${organizationId.value}/onboarding`,
    name: "navigation.onboarding",
    icon: Ship,
  },
  {
    key: "analyze",
    to: `/organizations/${organizationId.value}/analyze`,
    name: "navigation.analyze",
    icon: Sparkles,
  },
]);

// State for open submenu groups
const openGroups = ref<Record<string, boolean>>({});

// Initialize open state based on active route (ensure active child's parent is open)
watchEffect(() => {
  navigationEntries.value.forEach((entry) => {
    if (!entry.children) return;
    const anyActive = entry.children.some((c) => isActiveRoute(c.to));
    // Also open if the parent entry route itself is active
    const parentActive = isActiveRoute(entry.to);
    if (parentActive || anyActive) openGroups.value[entry.key] = true;
  });
});

const isGroupOpen = (entry: NavigationEntry) => {
  if (!entry.children) return false;
  return !!openGroups.value[entry.key];
};

const toggleGroup = (key: string) => {
  openGroups.value[key] = !openGroups.value[key];
};
</script>

<style scoped>
/* Simple fade for submenu appearance */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
